import * as angular from "angular";
import * as moment from "moment";
import {TestImportMatchStatus, StudentMatchStatus} from "../models/test-import-match-status.model";
import {Column} from "../models/column.model";
import {TestImportService} from "../services/test-import/test-import.service";
import {Match} from "../models/match.model";
import {ImportReportStatus, StudentImportPostOptions} from "../models/import-status.model";
import { ImportStatusService } from "../services/import-status/import-status.service";

const IMPORT_PROGRESS_INIT_DATA = "testImportProgress";
const MATCHES_INIT_DATA = "matches";
const HAS_INITIAL_IMPORT_STARTED = "hasInitialImportStarted";
const INIT_IMPORT_STATUS = "importStatus";
const IS_NEW_EXPERIENCE = "isNewExperience";

export class StudentTestImportController {
    progressWasChecked: boolean;
    testImportStatusData: TestImportMatchStatus;
    columns: Column[];
    error: boolean;
    matches: Match[];
    hasInitialImportStarted: boolean;
    hasInactiveStudentsWarning: boolean = false;
    isNewExperience: boolean;
    isTestImportStarted: boolean = false;
    importReportStatus: ImportReportStatus;
    isTestImportSuccessAlertVisible: boolean = false;
    testImportRedoTime: string;
    isPromptVisible: boolean = false;
    isAllowedToStartImport: boolean = false;
    checkboxChecked: boolean = false;
    rowsCheckedState: {[key: string]: boolean};
    rows: StudentMatchStatus[];
    idType: string = this.feature.defaultStudentMatchCriteria;
    redoMessage = false;

    /* @ngInject */
    constructor(
        private $routeParams: { districtId: string },
        private $route: angular.route.IRouteService,
        private $interval: angular.IIntervalService,
        private testImportService: TestImportService,
        private importStatusService: ImportStatusService,
        private $location: angular.ILocationService,
        private studentInactiveMaxPercentage,
        private hasInactiveWarning,
        private $timeout: angular.ITimeoutService,
        public feature: {[key:string]: string}
    ) {
        this.isNewExperience = this.$route.current.locals[IS_NEW_EXPERIENCE];
    }

    $onInit() {
        this.testImportStatusData = this.$route.current.locals[IMPORT_PROGRESS_INIT_DATA];
        this.matches = this.$route.current.locals[MATCHES_INIT_DATA];
        this.hasInitialImportStarted = this.$route.current.locals[HAS_INITIAL_IMPORT_STARTED];
        this.importReportStatus = this.$route.current.locals[INIT_IMPORT_STATUS];
        this.idType = this.testImportStatusData && this.testImportStatusData.districtMatchStatus 
            && this.testImportStatusData.districtMatchStatus.matchCriteria 
            ? this.testImportStatusData.districtMatchStatus.matchCriteria : this.feature.defaultStudentMatchCriteria;
        this.initRows();
        this.rowsCheckedState = this.createRowsCheckedState();
        this.testImportRedoTime = this.testImportService.getLastStartedOnTimeForRedo();

        this.renderTestImportResultTable();

        this.handleInactiveStudentsWarningMessage();
    }

    private initRows() {
        if (!this.testImportStatusData.studentMatchStatuses) { return; }

        this.rows = this.testImportStatusData.studentMatchStatuses
            .map((school: StudentMatchStatus) => {
                this.processStatus(school);
                return school;
            });
    }

    /* istanbul ignore next */
    private renderMatchedStudentsHtml = (params) => {
        params.$scope.vm = this;
        return `
            {{ data.totalStudentsMatched }}
            <download-csv
                ng-if="vm.isTestImportComplete()"
                district="vm.testImportStatusData.districtId"
                school="data.schoolId"
                status="matched"
                import-type="student"
            ></download-csv>
        `;
    }

    /* istanbul ignore next */
    private renderDeactivatedStudentsHtml = (params) => {
        params.$scope.vm = this;
        return `
            {{ data.totalStudentsDeactivated }}
            <download-csv
                ng-if="vm.isTestImportComplete()"
                district="vm.testImportStatusData.districtId"
                school="data.schoolId"
                status="deactivated"
                import-type="student"
            ></download-csv>
        `;
    }

    /* istanbul ignore next */
    private renderCreatedStudentsHtml = (params) => {
        params.$scope.vm = this;
        return `
            {{ data.totalStudentsCreated }}
            <download-csv
                ng-if="vm.isTestImportComplete()"
                district="vm.testImportStatusData.districtId"
                school="data.schoolId"
                status="created"
                import-type="student"
            ></download-csv>
        `;
    }

    private renderTransferStudentsHtml = (params) => {
        params.$scope.vm = this;
        return `
            {{ data.totalStudentsTransferred }}
            <download-csv
                ng-if="vm.isTestImportComplete()"
                district="vm.testImportStatusData.districtId"
                school="data.schoolId"
                status="transferred"
                import-type="student"
            ></download-csv>
        `;
    }

    private renderSchoolNameHtml = (params) => {
        params.$scope.vm = this;
        if(params.data.meta) {
            return `<div class="test-import__table-alert"  > {{ data.schoolName }}</div>`;
        }
        return `{{ data.schoolName }}`;
    }

    private renderTestImportResultTable() {

        this.columns = [
            { headerName: "School", field: "schoolName", multiLine: true, cellRenderer: this.renderSchoolNameHtml },
            { headerName: "Status", field: "status", multiLine: true },
            {
                headerName: "Students Made Inactive", field: "totalStudentsDeactivated",
                cellRenderer: this.renderDeactivatedStudentsHtml
            },
            { headerName: "Students Created", field: "totalStudentsCreated", cellRenderer: this.renderCreatedStudentsHtml },
            { headerName: "Students Matched", field: "totalStudentsMatched", cellRenderer: this.renderMatchedStudentsHtml },
            {
                headerName: "Students Transferred", field: "totalStudentsTransferred",
                cellRenderer: this.renderTransferStudentsHtml
            }
        ];
    }

    showStatusTable() {
        return !this.redoMessage && this.testImportStatusData &&
            (this.testImportStatusData.studentMatchStatuses && this.testImportStatusData.studentMatchStatuses.length > 0);
    }

    isTestImportComplete(): boolean {
        if (!this.testImportStatusData) { return false; }

        if (this.testImportRedoTime && this.testImportRedoTime === this.testImportStatusData.startedOn) {
            return false;
        }

        const districtMatchStatus = this.testImportStatusData.districtMatchStatus;
        if (!districtMatchStatus) { return false; }

        return districtMatchStatus.totalSchoolsCompleted === districtMatchStatus.totalSchools
            && !!this.testImportStatusData.completedOn;
    }

    isTestImportNotStarted(): boolean {
        return !this.testImportRedoTime && !(this.testImportStatusData && this.testImportStatusData.startedOn);
    }

    isTestImportInProgress(): boolean {
        if (this.testImportRedoTime && this.testImportRedoTime === this.testImportStatusData.startedOn) {
            return true;
        }
        return !this.isTestImportNotStarted() && !this.isTestImportComplete();
    }

    checkProgress(): void {
        this.progressWasChecked = true;
        this.testImportService.getTestImportProgress(this.$route.current.params.districtId).then(testImport => {
            this.testImportStatusData = testImport;
            this.processStudentMatchStatuses();
            this.isTestImportSuccessAlertVisible = this.isTestImportComplete();
            this.initRows();
            this.rowsCheckedState = this.createRowsCheckedState();
        });

        this.$timeout(() => {
            this.progressWasChecked = false;
        }, 5000);
    }

    startTestImport(): void {
        this.testImportService.matchStudents(this.$route.current.params.districtId);

        this.isTestImportStarted = true;
        this.initTestImportStatusCheck();
    }

    isRedoAvailable(): boolean {
        if (this.isNewExperience) { return true; }
        
        if (!this.isTestImportComplete()) {
            return true;
        }
        return !!this.matches && this.isTestImportComplete()
            && this.matches.filter(match => match.status === "IMPORT_COMPLETE").length === 0;
    }

    initTestImportStatusCheck() {
        // TODO: Cancel this interval onDestroy or switch to $timeout
        let statusApiUpdateInterval;
        statusApiUpdateInterval = this.$interval(() =>
            this.testTestImportStatusIntervalFrame(statusApiUpdateInterval), 2000);

        this.processStudentMatchStatuses();
    }

    clearError() { this.error = false; }

    private processStudentMatchStatuses() {
        if (this.testImportStatusData.studentMatchStatuses && this.testImportStatusData.studentMatchStatuses.length > 0) {
            this.testImportStatusData.studentMatchStatuses.map(studentMatchStatusData => {
                this.processStatus(studentMatchStatusData);
            });
            
            this.handleInactiveStudentsWarningMessage();
        }
    }

    handleInactiveStudentsWarningMessage(): void {
        if (!this.testImportStatusData || !this.testImportStatusData.studentMatchStatuses) { return; }
        this.hasInactiveStudentsWarning = false;
        this.testImportStatusData.studentMatchStatuses.forEach((rowStatus: any) => {
            const {totalStudentsCreated, totalStudentsMatched, totalStudentsDeactivated, totalStudentsTransferred} = rowStatus;
            const totalStudentsMadeInactiveAndTransferred: number = totalStudentsDeactivated + totalStudentsTransferred;
            const totalStudents: number = totalStudentsMadeInactiveAndTransferred + totalStudentsCreated + totalStudentsMatched;

            if(this.hasInactiveWarning && (
                totalStudentsMadeInactiveAndTransferred &&
                totalStudentsMadeInactiveAndTransferred >= (totalStudents * this.studentInactiveMaxPercentage)
            )) {
                rowStatus.meta = {rowStatus: "WARNING"};
                this.hasInactiveStudentsWarning = true;
            }
        });
    }

    processStatus(sms: StudentMatchStatus) {
        let status;

        if (sms.totalStudentsProcessed === null) {
            status = "Waiting for test import to begin";
        } else if (sms.totalStudentsProcessed >= 0 && sms.totalStudentsProcessed < sms.totalStudents) {
            status = `Test import in progress... ${sms.totalStudentsProcessed} of ${sms.totalStudents} student records completed...`;
        } else if (sms.totalStudentsProcessed === sms.totalStudents) {
            status = "Test import completed";
        }

        sms.status = status;
    }

    private testTestImportStatusIntervalFrame(statusApiUpdateInterval) {
        if (this.isTestImportStatusError()) {
            this.error = true;
            this.$interval.cancel(statusApiUpdateInterval);
            return;
        }

        if ((!this.isNewExperience && this.isTestImportComplete()) 
            || (this.isNewExperience && this.isTestImportInProgress())) {
            this.testImportService.setLastStartedOnTimeForRedo(null);
            this.$interval.cancel(statusApiUpdateInterval);
            this.initRows();
            this.renderTestImportResultTable();
        } else {
            this.testImportService.getTestImportProgress(this.$route.current.params.districtId).then(testImport => {
                this.testImportStatusData = testImport;
                this.processStudentMatchStatuses();
            });
        }
    }

    isTestImportStatusError(): boolean {
        if (!this.testImportStatusData) { return false; }
        return this.testImportStatusData.error;
    }

    getProgressBarWidth() {
        let progress = 0;
        const tisd = this.testImportStatusData;
        if (tisd == null) { return; }

        const dms = tisd.districtMatchStatus;
        const csfs = tisd.cleverStudentFetchStatuses;

        if (dms == null || csfs == null || !dms ||!csfs) {
            return "1%";
        }

        csfs.forEach(element => {
            if (element.fetched) {
                progress += (1 / dms.totalSchools) * 10;
            }
        });

        if (csfs !== null && dms.totalSchoolsCompleted > 0) {
            progress += (dms.totalSchoolsCompleted / dms.totalSchools) * 90;
        }

        return `${progress}%`;
    }

    maybeRouteToImportRecords() {
        return this.isTestImportComplete() ?
            this.$location.path(`${this.$routeParams.districtId}/student-import-records`) :
            undefined;
    }

    startImportStep(): void {
        this.isNewExperience ? this.openPrompt() : this.maybeRouteToImportRecords();
    }

    maybeShowRedoMessage() {
        if (!this.importReportStatus) {
            this.redoMatchStatusAndGetTestImportProgress();
            return;
        }
        this.redoMessage = true;
    }

    redoMatchStatusAndGetTestImportProgress() {
        // Hide table, decrease progressbar width + update header text
        this.testImportStatusData.districtMatchStatus.totalSchoolsCompleted = 0;
        this.testImportStatusData.studentMatchStatuses = [];
        this.testImportStatusData.cleverStudentFetchStatuses = [];
        this.hasInactiveStudentsWarning = false;
        this.redoMessage = false;
        this.testImportService.setLastStartedOnTimeForRedo(this.testImportStatusData.startedOn);
        this.testImportRedoTime = this.testImportStatusData.startedOn;

        this.testImportService.redoMatchStudents(this.$route.current.params.districtId, this.idType)
            .then(() => this.testImportService.getTestImportProgress(this.$routeParams.districtId))
            .then((res: TestImportMatchStatus) => {
                this.testImportStatusData = res;
                this.initTestImportStatusCheck();
            });
        this.renderTestImportResultTable();
    }

    isReadyToContinue(): boolean {
        return this.isTestImportComplete() &&
            !(this.isRedoTestImportRequiredBeforeInitialImport() || this.isRedoTestImportRequiredAfterInitialImport());
    }

    isContinueTooltipVisible(): boolean {
        return this.isRedoTestImportRequiredBeforeInitialImport() || this.isRedoTestImportRequiredAfterInitialImport();
    }

    isRedoTestImportRequiredBeforeInitialImport(): boolean {
        return this.testImportStatusData.hoursBetweenLastTestImportAndNow 
            >= this.testImportStatusData.testImportHoursBeforeInitialImport && !this.importReportStatus;
    }

    isRedoTestImportRequiredAfterInitialImport(): boolean {
        return this.testImportStatusData.hoursBetweenLastTestImportAndNow >= this.testImportStatusData.testImportHoursBeforeReimport &&
            (this.importReportStatus && this.importReportStatus.completedOn != null);
    }

    showRedoTooltipMessage(): string {
        if (this.isRedoTestImportRequiredBeforeInitialImport()) {
            return `It has been over ${this.testImportStatusData.testImportHoursBeforeInitialImport} hours since the test import. 
                A test import should be re-run for an accurate data import.`;
        } else if (this.isRedoTestImportRequiredAfterInitialImport()) {
            return `It has been over ${this.testImportStatusData.testImportHoursBeforeReimport} hours since the test import. 
                A test import should be re-run for an accurate data import.`;
        }
    }

    shouldShowWarning(): boolean {
        return this.isTestImportComplete() && this.hasInactiveStudentsWarning && !this.redoMessage;
    }

    cancelRedoIntro() {
        this.redoMessage = false;
    }

    getTestImportCompleteDate(): string {
        if (!this.testImportStatusData) { return; }

        const momentLocal = (moment as any).tz(this.testImportStatusData.completedOn, "UTC").local();
        return `${momentLocal.format("MMM DD, YYYY")} at ${momentLocal.format("hh:mm A")}.`;
    }

    getImportCompleteDate(): string {
        if (!(this.importReportStatus && this.importReportStatus.completedOn)) { return; }

        const momentLocal = (moment as any).tz(this.importReportStatus.completedOn, "UTC").local();
        return `${momentLocal.format("MMM DD, YYYY")} at ${momentLocal.format("hh:mm A")}.`;
    }

    isRedo(): boolean {
        return !!(this.importReportStatus && this.importReportStatus.completedOn 
            && new Date(this.testImportStatusData.completedOn) > new Date(this.importReportStatus.completedOn));
    }

    startImport() {
        const options: StudentImportPostOptions = {
            districtId: this.$routeParams.districtId,
            commandType: "MIGRATION",
            schoolsToImport: this.testImportStatusData.studentMatchStatuses.map(a => a.schoolId)
        };

        this.importStatusService.post(options).then(() => {
            this.maybeRouteToImportRecords();
        });
    }

    openPrompt() { this.isPromptVisible = true; }
    togglePrompt() { this.isPromptVisible = !this.isPromptVisible; }

    toggleAllowDisallowStudentImport(): void {
        this.isAllowedToStartImport = this.checkboxChecked;
    }

    private createRowsCheckedState(): {[key: string]: boolean} {
        if (!this.rows) { return; }

        return this.rows.reduce((acc, next) => {
            acc[`${next.schoolName}`] = this.isStatusNotImported(next.status);
            return acc;
        }, Object.create(null));
    }

    private isStatusNotImported(status: string):boolean {
        return status !== "Imported";
    }

    private getSelectedRowIds(): string[] {
        return this.testImportStatusData.studentMatchStatuses.reduce((acc, row) => {
            if (this.rowsCheckedState[row.schoolName] === true) {
                acc.push(row.schoolId);
            }
            return acc;
        }, []);
    }

    startStudentImport() {
        this.isPromptVisible = false;

        if (this.isAllowedToStartImport) {
            const options: StudentImportPostOptions = {
                districtId: this.$routeParams.districtId,
                commandType: "MIGRATION",
                schoolsToImport: this.getSelectedRowIds()
            };

            this.importStatusService.post(options);
            return this.$location.path(`${this.$routeParams.districtId}/student-import-records`);
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/student-test-import/student-test-import.controller.ts